import type { FormattedUser } from '../../hooks/useOrganizationUsers';
import { formatPhoneNumber } from '../formatters';

interface UserOption {
  label: string;
  value: string;
}

/**
 * Builds transfer line select options from a list of users.
 * - Filters out users without a phone number
 * - Sorts by firstName then lastName
 * - Merges in any existing flow transfer numbers not already covered by a user
 */
export const stripPhone = (phone: string) => phone.replace(/\D/g, '').replace(/^1(\d{10})$/, '$1');

export const buildTransferLineOptions = (
  users: FormattedUser[],
  existingNumbers?: Record<string, string>
): UserOption[] => {
  const seenPhones = new Set<string>();
  const userOptions = users
    .filter((u) => u.phone_number && u.phone_number.trim() !== '')
    .sort((a, b) => {
      const first = (a.firstName ?? '').localeCompare(b.firstName ?? '');
      return first !== 0 ? first : (a.lastName ?? '').localeCompare(b.lastName ?? '');
    })
    .reduce((acc, u) => {
      const norm = stripPhone(u.phone_number);
      if (seenPhones.has(norm)) return acc;
      seenPhones.add(norm);
      const name = `${u.firstName} ${u.lastName}`.trim() || u.email;
      acc.push({ value: u.phone_number, label: `${name} (${formatPhoneNumber(u.phone_number)})` });
      return acc;
    }, [] as UserOption[]);

  if (!existingNumbers) return userOptions;

  // Deduplicate by normalizing phone digits — avoids doubles from format differences (+1 vs no prefix)
  const existingOptions = Object.entries(existingNumbers)
    .filter(([, number]) => !userOptions.some((u) => stripPhone(u.value) === stripPhone(number)))
    .map(([name, number]) => ({
      value: number,
      label: number ? `${name} (${formatPhoneNumber(number)})` : name,
    }));

  return [...userOptions, ...existingOptions];
};

/**
 * Formats transfer numbers into a Record with name as key and phone number as value.
 * @param users Array of user options with label (name) and value (phone number)
 * @returns Record<string, string> with format { "First Last": "phoneNumber" }
 */
export const formatTransferNumbers = (users: UserOption[]): Record<string, string> => {
  return users.reduce(
    (acc, user) => {
      // Support labels with or without trailing "(phone)" parenthetical
      const nameMatch = user.label.match(/^(.*?)\s*(?:\(.*\))?$/);
      const fullName = nameMatch ? nameMatch[1].trim() : user.label.trim();
      if (fullName) {
        acc[fullName] = user.value;
      }
      return acc;
    },
    {} as Record<string, string>
  );
};
